import * as React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { AdminGeneralInfoResult } from '../../../../models/adminGeneral';
import { formatRubles } from '../../../../common/money';
import { Delta } from '../Delta';
import { useNavigate } from 'react-router-dom';

export type OrdersCardProps = { data: AdminGeneralInfoResult | null };

export const OrdersCard: React.FC<OrdersCardProps> = React.memo(({ data }) => {
    const navigate = useNavigate();
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/app/orders')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/orders'); } }}
            >
                <ReceiptLongRoundedIcon fontSize="small" />
                <Typography level="title-md">Ордера</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="body-sm">Количество</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Количество ордеров за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.orderCount ?? 0) > (data.orderCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${data.orderCount}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${data.orderCountLastPeriod})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.orderCount ?? 0} previous={data.orderCountLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">Сумма</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Сумма ордеров за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.orderSum ?? 0) > (data.orderSumLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.orderSum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.orderSumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.orderSum ?? 0} previous={data.orderSumLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box sx={{ flexBasis: '100%', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Профит по ордерам за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                                <TrendingUpRoundedIcon fontSize="small" sx={{ color: (data && (data.orderProfit ?? 0) > (data.orderProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }} />
                                <Typography noWrap level="title-lg" sx={{ color: (data && (data.orderProfit ?? 0) > (data.orderProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.orderProfit ?? 0)}` : '-'}
                                </Typography>
                                <Typography noWrap level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.orderProfitLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.orderProfit ?? 0} previous={data.orderProfitLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (кол-во)</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Среднее кол-во ордеров в день этого (прошлого) периодов" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.orderInDayCount ?? 0) > (data.orderInDayCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${data.orderInDayCount ?? 0}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${data.orderInDayCountLastPeriod ?? 0})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.orderInDayCount ?? 0} previous={data.orderInDayCountLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (сумма)</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap' }}>
                        <Tooltip title="Средняя сумма ордеров в день этого (прошлого) периодов" disablePortal={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 1 }}>
                                <Typography level="title-lg" sx={{ color: (data && (data.orderInDaySum ?? 0) > (data.orderInDaySumLastPeriod ?? 0)) ? 'success.600' : 'danger.600' }}>
                                    {data ? `+${formatRubles(data.orderInDaySum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                    {data ? `(+${formatRubles(data.orderInDaySumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta current={data.orderInDaySum ?? 0} previous={data.orderInDaySumLastPeriod ?? 0} />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
            </Box>
        </Sheet>
    );
});

OrdersCard.displayName = 'OrdersCard';


